import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { isNativeApp } from '../../../utils/platform';

const DriverOnboarding = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [checkingStripe, setCheckingStripe] = useState(false);
  const [stripeMessage, setStripeMessage] = useState('');
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [completingOnboarding, setCompletingOnboarding] = useState(false);
  const [skippingDayBooking, setSkippingDayBooking] = useState(false);
  const [skippingSubscription, setSkippingSubscription] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchOnboardingStatus();
  }, []);

  const fetchOnboardingStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/onboarding-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(response.data);
    } catch (error) {
      console.error('Error fetching onboarding status:', error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToDashboard = () => {
    const tutorialShown = localStorage.getItem('driver_tutorial_completed');
    if (!tutorialShown) {
      navigate('/driver/dashboard?showTutorial=true');
    } else {
      navigate('/driver/dashboard');
    }
  };

  const handleGoToDashboard = () => {
    navigateToDashboard();
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    setResendSuccess(false);
    setVerificationMessage('');
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/resend-verification`, {
        email: user?.email || localStorage.getItem('user_email')
      });
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to resend verification email';
      setVerificationMessage(errorMsg);
      setTimeout(() => setVerificationMessage(''), 5000);
    } finally {
      setResendingEmail(false);
    }
  };

  const handleCheckVerification = async () => {
    setCheckingVerification(true);
    setVerificationMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/onboarding-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(response.data);

      if (response.data.email_verified) {
        setVerificationMessage('Email verified! You can proceed to the next step.');
      } else {
        setVerificationMessage('Email not verified yet. Please check your inbox (and spam folder) for the verification link.');
      }
      setTimeout(() => setVerificationMessage(''), 5000);
    } catch (error) {
      console.error('Error checking verification status:', error);
      setVerificationMessage('Failed to check verification status. Please try again.');
      setTimeout(() => setVerificationMessage(''), 5000);
    } finally {
      setCheckingVerification(false);
    }
  };

  const handleCheckStripeStatus = async () => {
    setCheckingStripe(true);
    setStripeMessage('');
    try {
      const token = localStorage.getItem('token');

      // First, call verify-onboarding to check Stripe API and update database
      const verifyResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/stripe/verify-onboarding`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Then refresh the onboarding status
      const statusResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/drivers/onboarding-status`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStatus(statusResponse.data);

      if (verifyResponse.data.stripe_onboarding_complete) {
        setStripeMessage('Stripe account connected! You can proceed to the next step.');
      } else if (verifyResponse.data.stripe_connected_account_id) {
        // Show what's missing
        const missing = [];
        if (!verifyResponse.data.charges_enabled) missing.push('charges');
        if (!verifyResponse.data.payouts_enabled) missing.push('payouts');
        setStripeMessage(`Stripe account connected but setup not complete. Missing: ${missing.join(', ')}. Please finish the Stripe setup.`);
      } else {
        setStripeMessage('Stripe account not connected yet. Please complete the Stripe onboarding process.');
      }
      setTimeout(() => setStripeMessage(''), 8000);
    } catch (error) {
      console.error('Error checking Stripe status:', error);
      const errorDetail = error.response?.data?.detail || 'Failed to check Stripe status. Please try again.';
      setStripeMessage(errorDetail);
      setTimeout(() => setStripeMessage(''), 5000);
    } finally {
      setCheckingStripe(false);
    }
  };

  const handleSkipDayBooking = async () => {
    setSkippingDayBooking(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/skip-day-booking`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh status
      await fetchOnboardingStatus();
    } catch (error) {
      console.error('Error skipping day booking:', error);
      alert('Failed to skip. Please try again.');
    } finally {
      setSkippingDayBooking(false);
    }
  };

  const handleSkipSubscription = async () => {
    setSkippingSubscription(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/skip-subscription`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Refresh status
      await fetchOnboardingStatus();
    } catch (error) {
      console.error('Error skipping subscription:', error);
      alert('Failed to skip. Please try again.');
    } finally {
      setSkippingSubscription(false);
    }
  };

  const handleCompleteOnboarding = async () => {
    setCompletingOnboarding(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/drivers/complete-onboarding`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // Show congratulations modal
      setShowCongratulations(true);
    } catch (error) {
      console.error('Error completing onboarding:', error);
      const errorDetail = error.response?.data?.detail || 'Failed to complete onboarding. Please ensure all steps are done.';
      alert(errorDetail);
    } finally {
      setCompletingOnboarding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your onboarding status...</p>
        </div>
      </div>
    );
  }

  // Determine step states
  const steps = [
    {
      id: 1,
      title: 'Complete Sign-up Form',
      description: 'Create your driver account',
      completed: true,
      current: false,
      locked: false,
      action: null
    },
    {
      id: 2,
      title: 'Verify Email',
      description: 'Check your inbox and click the verification link',
      completed: status?.email_verified,
      current: !status?.email_verified,
      locked: false,
      action: 'resend'
    },
    {
      id: 3,
      title: 'Complete Profile Details',
      description: 'Add your profile picture, vehicle information, and bio',
      completed: status?.profile_complete,
      current: status?.email_verified && !status?.profile_complete,
      locked: !status?.email_verified,
      action: '/driver/onboarding/profile'
    },
    {
      id: 4,
      title: 'Connect Stripe Account',
      description: 'Set up payouts to receive your earnings',
      completed: status?.stripe_onboarding_complete,
      current: status?.profile_complete && !status?.stripe_onboarding_complete,
      locked: !status?.profile_complete,
      action: 'stripe_onboarding'
    },
    {
      id: 5,
      title: 'Set Your Availability',
      description: 'Choose when you can accept ride requests',
      completed: status?.availability_set,
      current: status?.stripe_onboarding_complete && !status?.availability_set,
      locked: !status?.stripe_onboarding_complete,
      action: '/driver/availability?onboarding=true'
    },
    {
      id: 6,
      title: 'Set Your Pricing',
      description: 'Set your per-mile rates for rides',
      completed: status?.pricing_set,
      current: status?.availability_set && !status?.pricing_set,
      locked: !status?.availability_set,
      action: '/driver/pricing?onboarding=true'
    },
  ];

  // Optional steps - now require explicit action (set up OR skip)
  const optionalSteps = [
    {
      id: 'optional-1',
      title: 'Half-Day / Full-Day Reservations',
      description: 'Earn more by offering day rates to riders',
      reviewed: status?.day_booking_reviewed,
      configured: status?.accepts_day_bookings && status?.day_rate_pricing_set,
      locked: !status?.pricing_set,
      action: '/driver/day-rate-pricing?onboarding=true',
      skipAction: handleSkipDayBooking,
      skipping: skippingDayBooking
    },
    ...(isNativeApp() ? [] : [{
      id: 'optional-2',
      title: 'Select a Subscription Plan',
      description: 'Upgrade to lower your commission rate',
      reviewed: status?.subscription_reviewed,
      configured: (status?.subscription_tier_id || 5) !== 5,
      locked: false,
      action: '/driver/subscription?onboarding=true',
      skipAction: handleSkipSubscription,
      skipping: skippingSubscription
    }])
  ];

  const completedSteps = steps.filter(s => s.completed).length;
  const totalSteps = steps.length;
  const progress = Math.round((completedSteps / totalSteps) * 100);

  // Check if ready to complete onboarding
  const requiredStepsComplete = status?.required_steps_complete;
  const optionalStepsReviewed = status?.optional_steps_reviewed;
  const canCompleteOnboarding = requiredStepsComplete && optionalStepsReviewed;
  const isOnboardingComplete = status?.onboarding_complete;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      {/* Congratulations Modal */}
      {showCongratulations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 text-center transform animate-bounce-in">
            {/* Celebration Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mb-3">
              Congratulations!
            </h2>
            <p className="text-gray-600 mb-6">
              You've successfully completed your driver setup! You're now ready to start accepting ride requests and earning money with RideHowl.
            </p>

            <div className="bg-green-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-green-800">
                <strong>What's next?</strong> We'll give you a quick tour of your dashboard so you know where everything is.
              </p>
            </div>

            <button
              onClick={handleGoToDashboard}
              className="w-full py-3 px-6 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
            >
              Take Me to My Dashboard
            </button>
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        {/* Self-Service Model Callout */}
        {!isOnboardingComplete && (
          <div className="bg-indigo-50 border-2 border-indigo-300 rounded-xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                <svg className="w-7 h-7 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
                </svg>
              </div>
              <div>
                <p className="text-indigo-900 font-bold text-lg mb-1">
                  RideHowl doesn't send you riders — you bring your own.
                </p>
                <p className="text-indigo-800 text-base">
                  Think of it like running your own driving business, with our tools handling the bookings, payments, and scheduling for you.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {isOnboardingComplete ? "You're All Set!" : "Welcome to RideHowl!"}
          </h1>
          <p className="text-gray-600">
            {isOnboardingComplete
              ? "Your driver account is fully set up. Review your completed steps below or head to your dashboard."
              : "Complete these steps to activate your driver account and start accepting rides."
            }
          </p>
        </div>

        {/* Warning Banner */}
        {!isOnboardingComplete && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6 flex items-center gap-3">
            <svg className="w-6 h-6 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <p className="text-amber-800 text-sm font-medium">
              Do not close out of this screen, or your progress will be lost. Complete all required steps before leaving.
            </p>
          </div>
        )}

        {/* Progress Bar */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Setup Progress</span>
            <span className="text-sm font-medium text-green-600">{progress}% Complete</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className="bg-green-600 h-3 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {completedSteps} of {totalSteps} required steps completed
          </p>
        </div>

        {/* Steps Checklist */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="divide-y divide-gray-200">
            {steps.map((step) => (
              <div
                key={step.id}
                className={`p-5 ${step.locked ? 'bg-gray-50 opacity-60' : ''} ${step.current ? 'bg-green-50' : ''}`}
              >
                <div className="flex items-start gap-4">
                  {/* Step Number / Status */}
                  <div className="flex-shrink-0">
                    {step.completed ? (
                      <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : step.current ? (
                      <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center">
                        <span className="text-white font-semibold text-sm">{step.id}</span>
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                        <span className="text-gray-600 font-semibold text-sm">{step.id}</span>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-grow">
                    <h3 className={`font-semibold ${step.completed ? 'text-green-700' : step.current ? 'text-green-800' : 'text-gray-700'}`}>
                      {step.title}
                    </h3>
                    <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>

                    {/* Action Button */}
                    {step.current && !step.completed && (
                      <div className="mt-3">
                        {step.action === 'resend' ? (
                          <div>
                            <button
                              onClick={handleResendVerification}
                              disabled={resendingEmail}
                              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                            >
                              {resendingEmail ? (
                                <>
                                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                  Sending...
                                </>
                              ) : (
                                'Resend Verification Email'
                              )}
                            </button>
                            {resendSuccess && (
                              <p className="text-sm text-green-600 mt-2">
                                Verification email sent! Check your inbox.
                              </p>
                            )}
                            <button
                              onClick={handleCheckVerification}
                              disabled={checkingVerification}
                              className="ml-3 text-sm text-green-600 hover:text-green-700 underline disabled:opacity-50"
                            >
                              {checkingVerification ? 'Checking...' : "I've verified my email"}
                            </button>
                            {verificationMessage && (
                              <p className={`text-sm mt-2 ${
                                verificationMessage.includes('verified!')
                                  ? 'text-green-600'
                                  : verificationMessage.includes('not verified')
                                    ? 'text-amber-600'
                                    : 'text-red-600'
                              }`}>
                                {verificationMessage}
                              </p>
                            )}
                          </div>
                        ) : step.action === 'stripe_onboarding' ? (
                          <div>
                            <Link
                              to="/driver/stripe-onboarding"
                              className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                              Connect Stripe Account
                              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                            <button
                              onClick={handleCheckStripeStatus}
                              disabled={checkingStripe}
                              className="ml-3 text-sm text-green-600 hover:text-green-700 underline disabled:opacity-50"
                            >
                              {checkingStripe ? 'Checking...' : "I've completed Stripe setup"}
                            </button>
                            {stripeMessage && (
                              <p className={`text-sm mt-2 ${
                                stripeMessage.includes('connected!')
                                  ? 'text-green-600'
                                  : stripeMessage.includes('not complete')
                                    ? 'text-amber-600'
                                    : 'text-red-600'
                              }`}>
                                {stripeMessage}
                              </p>
                            )}
                          </div>
                        ) : (
                          <Link
                            to={step.action}
                            className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                          >
                            Continue
                            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                          </Link>
                        )}
                      </div>
                    )}

                    {/* Completed badge */}
                    {step.completed && (
                      <span className="inline-block mt-2 text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded">
                        Completed
                      </span>
                    )}

                    {/* Locked message */}
                    {step.locked && (
                      <span className="inline-block mt-2 text-xs text-gray-500">
                        Complete previous steps first
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Optional Steps Header */}
            <div className="p-4 bg-blue-100 border-t-2 border-blue-300">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm font-semibold text-blue-800">
                  Optional Steps - Please review each option below
                </span>
              </div>
              <p className="text-xs text-blue-700 mt-1 ml-7">
                You can set these up now or skip them. You must choose an option for each before completing onboarding.
              </p>
            </div>

            {/* Optional Steps */}
            {optionalSteps.map((optStep) => (
              <div key={optStep.id} className={`p-5 bg-blue-50 ${optStep.locked ? 'opacity-60' : ''}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    {optStep.reviewed ? (
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-blue-200 flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                      </div>
                    )}
                  </div>

                  <div className="flex-grow">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-blue-800">{optStep.title}</h3>
                      <span className="text-xs font-medium text-blue-600 bg-blue-100 px-2 py-0.5 rounded">
                        Optional
                      </span>
                      {optStep.reviewed && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                          optStep.configured
                            ? 'text-green-600 bg-green-100'
                            : 'text-gray-600 bg-gray-200'
                        }`}>
                          {optStep.configured ? 'Configured' : 'Skipped'}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-blue-600 mt-0.5">{optStep.description}</p>

                    {!optStep.locked && !optStep.reviewed && (
                      <div className="flex items-center gap-3 mt-3 flex-wrap">
                        <Link
                          to={optStep.action}
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                        >
                          Set Up Now
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                          </svg>
                        </Link>
                        <button
                          onClick={optStep.skipAction}
                          disabled={optStep.skipping}
                          className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50"
                        >
                          {optStep.skipping ? (
                            <>
                              <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              Skipping...
                            </>
                          ) : (
                            'Skip for Now'
                          )}
                        </button>
                      </div>
                    )}

                    {optStep.reviewed && !optStep.configured && (
                      <Link
                        to={optStep.action}
                        className="inline-flex items-center mt-3 text-sm text-blue-700 hover:text-blue-800 font-medium"
                      >
                        Changed my mind - Set Up Now
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}

                    {optStep.reviewed && optStep.configured && (
                      <Link
                        to={optStep.action}
                        className="inline-flex items-center mt-3 text-sm text-blue-700 hover:text-blue-800 font-medium"
                      >
                        View/Edit Settings
                        <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    )}

                    {optStep.locked && (
                      <span className="inline-flex items-center mt-3 text-sm text-blue-400">
                        Complete required steps first
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Complete Onboarding Section */}
        {!isOnboardingComplete && (
          <div className={`mt-6 rounded-lg shadow-lg p-6 ${
            canCompleteOnboarding
              ? 'bg-gradient-to-r from-green-500 to-green-600'
              : 'bg-gray-200'
          }`}>
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <div className="flex-shrink-0">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                  canCompleteOnboarding ? 'bg-white bg-opacity-20' : 'bg-gray-300'
                }`}>
                  <svg className={`w-7 h-7 ${canCompleteOnboarding ? 'text-white' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow text-center sm:text-left">
                <h3 className={`text-lg font-bold ${canCompleteOnboarding ? 'text-white' : 'text-gray-600'}`}>
                  {canCompleteOnboarding ? 'Ready to Go!' : 'Almost There...'}
                </h3>
                <p className={`text-sm ${canCompleteOnboarding ? 'text-green-100' : 'text-gray-500'}`}>
                  {canCompleteOnboarding
                    ? 'All steps reviewed! Click the button to complete your onboarding.'
                    : 'Complete all required steps and review optional steps to finish.'}
                </p>
              </div>
              <button
                onClick={handleCompleteOnboarding}
                disabled={!canCompleteOnboarding || completingOnboarding}
                className={`flex-shrink-0 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  canCompleteOnboarding
                    ? 'bg-white text-green-600 hover:bg-green-50'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                } disabled:opacity-50`}
              >
                {completingOnboarding ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Completing...
                  </span>
                ) : (
                  'Complete Onboarding'
                )}
              </button>
            </div>
          </div>
        )}

        {/* Already Complete Section */}
        {isOnboardingComplete && (
          <div className="mt-6 bg-gradient-to-r from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div className="flex-grow">
                <h3 className="text-lg font-bold">All Set!</h3>
                <p className="text-green-100 text-sm">Your account is ready. Head to your dashboard to start accepting rides!</p>
              </div>
              <button
                onClick={handleGoToDashboard}
                className="flex-shrink-0 px-6 py-3 bg-white text-green-600 rounded-lg font-semibold hover:bg-green-50 transition-colors"
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            Need help? Contact us at{' '}
            <a href="mailto:support@ridehowl.com" className="text-green-600 hover:underline">
              support@ridehowl.com
            </a>
          </p>
        </div>
      </div>

    </div>
  );
};

export default DriverOnboarding;
